// Force HMR refresh
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useSEO } from "@/hooks/useSEO";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import LiveThreatTicker from "@/components/LiveThreatTicker";
import SocialProof from "@/components/SocialProof";
import ValueProposition from "@/components/ValueProposition";
import Testimonials from "@/components/Testimonials";

import ThreatsNearMe from "@/components/ThreatsNearMe";
import DailyBrief from "@/components/DailyBrief";
import SignalChain from "@/components/SignalChain";
import IntelFeed from "@/components/IntelFeed";
import CountryInstability from "@/components/CountryInstability";
import StrategicPosture from "@/components/StrategicPosture";
import MarketRadar from "@/components/MarketRadar";
import CryptoMonitor from "@/components/CryptoMonitor";
import ConflictMap from "@/components/ConflictMap";
import PersonalSafetyRadar from "@/components/PersonalSafetyRadar";
import SmartAlerts from "@/components/SmartAlerts";
import FlightRiskIndex from "@/components/FlightRiskIndex";
import SafeRoutes from "@/components/SafeRoutes";
import CountryEmergencyGuide from "@/components/CountryEmergencyGuide";
import MissileEarlyWarning from "@/components/MissileEarlyWarning";
import PanicAlerts from "@/components/PanicAlerts";
import PremiumBanner from "@/components/PremiumBanner";
import PremiumWatchlist from "@/components/PremiumWatchlist";
import NewsFeed from "@/components/NewsFeed";
import EmergencyKits from "@/components/EmergencyKits";
import DonationSection from "@/components/DonationSection";
import TrustCredibilitySection from "@/components/TrustCredibilitySection";
import LegalFooter from "@/components/LegalFooter";
import StickyUpgradeBar from "@/components/StickyUpgradeBar";
import ConflictAssistant from "@/components/ConflictAssistant";
import PredictionMarkets from "@/components/PredictionMarkets";
import LiveNewsTV from "@/components/LiveNewsTV";
import LiveWebcams from "@/components/LiveWebcams";
import IsraelSirens from "@/components/IsraelSirens";
import EarthquakeMonitor from "@/components/EarthquakeMonitor";
import ClimateAnomalies from "@/components/ClimateAnomalies";
import WorldBankData from "@/components/WorldBankData";
import ConflictEconomicImpact from "@/components/ConflictEconomicImpact";
import SupplyChainMonitor from "@/components/SupplyChainMonitor";
import AirlineIntelligence from "@/components/AirlineIntelligence";
import TechReadinessIndex from "@/components/TechReadinessIndex";
import ScrollToTop from "@/components/ScrollToTop";
import LiveViewerCount from "@/components/LiveViewerCount";
import PremiumPeek from "@/components/PremiumPeek";
import RecentPurchaseToast from "@/components/RecentPurchaseToast";
import ExitIntentModal from "@/components/ExitIntentModal";
import FlashOfferModal from "@/components/FlashOfferModal";
import ConflictTimeline from "@/components/ConflictTimeline";
import ArmsInventoryMonitor from "@/components/ArmsInventoryMonitor";
import TravelSafetyCheck from "@/components/TravelSafetyCheck";
import RefugeeTracker from "@/components/RefugeeTracker";
import WeeklyReport from "@/components/WeeklyReport";
import SanctionsTracker from "@/components/SanctionsTracker";
import CyberAttackMonitor from "@/components/CyberAttackMonitor";
import HistoricalConflictComparison from "@/components/HistoricalConflictComparison";
import NuclearThreatMonitor from "@/components/NuclearThreatMonitor";
import CriticalMaterialsMonitor from "@/components/CriticalMaterialsMonitor";
import EssentialGoodsScarcity from "@/components/EssentialGoodsScarcity";
import FAQ from "@/components/FAQ";
import PremiumToolsShowcase from "@/components/PremiumToolsShowcase";
import CrisisDashboard from "@/components/CrisisDashboard";
import DailyCrisisEmailSignup from "@/components/DailyCrisisEmailSignup";
import { Map, Link2, TrendingUp, CloudSun, Brain, Tv, Bell, ChevronLeft, ChevronRight, Gem } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

const TABS = [
  { id: "map", label: "Map & Threats", icon: Map },
  { id: "chain", label: "Signal Chain", icon: Link2 },
  { id: "markets", label: "Markets", icon: TrendingUp },
  { id: "resources", label: "Resources", icon: Gem },
  { id: "climate", label: "Climate & Seismic", icon: CloudSun },
  { id: "intel", label: "Intel & Analysis", icon: Brain },
  { id: "live", label: "Live Feeds", icon: Tv },
  { id: "alerts", label: "Alerts & Safety", icon: Bell },
] as const;

type TabId = (typeof TABS)[number]["id"];

const Index = () => {
  const [activeTab, setActiveTab] = useState<TabId>("map");
  const tabsRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  useSEO({
    title: "Global Conflict Monitor — Real-time OSINT Conflict Intelligence",
    description: "Track wars, missiles, markets, and safety risks in real time. 56 OSINT tools and <2 min alerts in one trusted intelligence dashboard.",
    path: "/",
  });

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsRef.current) {
      tabsRef.current.scrollBy({ left: direction === "left" ? -120 : 120, behavior: "smooth" });
    }
  };

  // Prevent browser scroll restoration and force scroll to top
  if (typeof window !== "undefined") {
    window.history.scrollRestoration = "manual";
  }

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    // Fallback for lazy-loaded content race conditions
    const t = setTimeout(() => window.scrollTo(0, 0), 0);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (tabsRef.current) {
      const activeBtn = tabsRef.current.querySelector(`[data-tab-id="${activeTab}"]`) as HTMLElement;
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
      }
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-background">
      <div className="scan-line" />
      <Navbar />
      <HeroSection />
      <LiveThreatTicker />
      <SocialProof />
      <PremiumToolsShowcase onSelectTab={setActiveTab} />
      <ValueProposition />
      <CrisisDashboard />

      {/* Tab Navigation */}
      <div data-tab-bar className="sticky top-[57px] z-30 border-y border-primary/20 bg-background/95 backdrop-blur-md shadow-[0_4px_20px_-4px_hsl(var(--primary)/0.1)]">
        <div className="container mx-auto px-2 sm:px-4">
          <div className="relative flex items-center">
            {isMobile && (
              <button
                onClick={() => scrollTabs("left")}
                className="absolute left-0 z-10 flex h-full items-center bg-gradient-to-r from-background via-background/80 to-transparent pr-2"
                aria-label="Scroll left"
              >
                <ChevronLeft className="h-4 w-4 text-muted-foreground" />
              </button>
            )}

            <div
              ref={tabsRef}
              className="flex overflow-x-auto scrollbar-none gap-0.5 sm:gap-1 py-2 sm:py-2.5 w-full"
              style={{ scrollbarWidth: "none" }}
            >
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    data-tab-id={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-1 sm:gap-1.5 whitespace-nowrap rounded-md px-2.5 sm:px-3.5 py-1.5 sm:py-2 text-[11px] sm:text-xs font-semibold transition-all active:scale-95 ${
                      isActive
                        ? "bg-primary/15 text-primary"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    }`}
                  >
                    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                    {tab.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>

            {isMobile && (
              <button
                onClick={() => scrollTabs("right")}
                className="absolute right-0 z-10 flex h-full items-center bg-gradient-to-l from-background via-background/80 to-transparent pl-2"
                aria-label="Scroll right"
              >
                <ChevronRight className="h-4 w-4 text-muted-foreground animate-[pulse_2s_ease-in-out_infinite]" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="min-h-[60vh]">
        {activeTab === "map" && (
          <>
            <ConflictMap />
            <ConflictTimeline />
            <div id="threats-near-me"><ThreatsNearMe /></div>
            <StrategicPosture />
            <PersonalSafetyRadar />
            <TravelSafetyCheck />
            <RefugeeTracker />
            <CountryInstability />
          </>
        )}
        {activeTab === "chain" && (
          <PremiumPeek sectionId="chain">
            <SignalChain />
            <ArmsInventoryMonitor />
            <CyberAttackMonitor />
            <NuclearThreatMonitor />
            <AirlineIntelligence />
            <TechReadinessIndex />
          </PremiumPeek>
        )}
        {activeTab === "markets" && (
          <PremiumPeek sectionId="markets">
            <MarketRadar />
            <CryptoMonitor />
            <PredictionMarkets />
            <ConflictEconomicImpact />
          </PremiumPeek>
        )}
        {activeTab === "resources" && (
          <PremiumPeek sectionId="resources">
            <EssentialGoodsScarcity />
            <CriticalMaterialsMonitor />
            <SupplyChainMonitor />
            <WorldBankData />
          </PremiumPeek>
        )}
        {activeTab === "climate" && (
          <>
            <EarthquakeMonitor />
            <ClimateAnomalies />
          </>
        )}
        {activeTab === "intel" && (
          <PremiumPeek sectionId="intel">
            <DailyBrief />
            <WeeklyReport />
            <IntelFeed />
            <HistoricalConflictComparison />
            <SanctionsTracker />
            <FlightRiskIndex />
            <MissileEarlyWarning />
            <IsraelSirens />
          </PremiumPeek>
        )}
        {activeTab === "live" && (
          <>
            <LiveNewsTV />
            <LiveWebcams />
            <NewsFeed />
          </>
        )}
        {activeTab === "alerts" && (
          <>
            <SmartAlerts />
            <SafeRoutes />
            <CountryEmergencyGuide />
            <PanicAlerts />
          </>
        )}
      </div>

      {/* Footer sections */}
      <PremiumWatchlist />
      <PremiumBanner />
      <Testimonials />
      <DailyCrisisEmailSignup />
      <EmergencyKits />
      <DonationSection />
      <FAQ />
      <TrustCredibilitySection />
      <LegalFooter />
      <StickyUpgradeBar />
      <ConflictAssistant />
      <RecentPurchaseToast />
      <ExitIntentModal />
      <FlashOfferModal />
      <LiveViewerCount />
      <ScrollToTop />
    </div>
  );
};

export default Index;
